import { createApi } from "@reduxjs/toolkit/query/react"

import { baseQueryWithAuth } from "utils/api"
import { prepareQueryParams } from "utils/prepareQueryParams"
import {
  AlertRuleConfiguration,
  ContractorAlertRuleUpdateBody,
  ContractorAlertRuleUpdateResponse,
} from "pages/contractor/MonitoringPage/alertRules.types"

type AlertRulesListResponse = {
  data: AlertRuleConfiguration[]
}

export type AlertRulesQueryParams = {
  name?: string
  orderColumn?: "name" | "is_enabled" | "triggered_devices_count"
  orderType?: "ASC" | "DESC"
}

export const alertRulesApi = createApi({
  reducerPath: "alertRules",
  tagTypes: ["alertRules"],
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getContractorAlertRules: builder.query<AlertRulesListResponse, AlertRulesQueryParams | void>({
      query: params => ({
        url: "/contractors/alert-rules",
        method: "GET",
        params: prepareQueryParams(params || {}),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["alertRules"],
    }),
    updateContractorAlertRule: builder.mutation<
      { data: ContractorAlertRuleUpdateResponse },
      { alertRuleId: string; body: ContractorAlertRuleUpdateBody }
    >({
      query: ({ alertRuleId, body }) => ({
        url: `/contractors/alert-rules/${alertRuleId}`,
        method: "PUT",
        body,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["alertRules"],
    }),
    getDeviceAlertRules: builder.query<AlertRulesListResponse, { deviceId: number } & AlertRulesQueryParams>({
      query: ({ deviceId, ...params }) => ({
        url: `/contractors/devices/${deviceId}/alert-rules`,
        method: "GET",
        params: prepareQueryParams(params),
        headers: { Accept: "application/json" },
      }),
      providesTags: ["alertRules"],
    }),
    updateDeviceAlertRule: builder.mutation<
      { data: AlertRuleConfiguration },
      { deviceId: number; alertRuleId: string; body: ContractorAlertRuleUpdateBody }
    >({
      query: ({ deviceId, alertRuleId, body }) => ({
        url: `/contractors/devices/${deviceId}/alert-rules/${alertRuleId}`,
        method: "PUT",
        body,
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["alertRules"],
    }),
    deleteDeviceAlertRule: builder.mutation<void, { deviceId: number; alertRuleId: string }>({
      query: ({ deviceId, alertRuleId }) => ({
        url: `/contractors/devices/${deviceId}/alert-rules/${alertRuleId}`,
        method: "DELETE",
        headers: { Accept: "application/json" },
      }),
      invalidatesTags: ["alertRules"],
    }),
  }),
})

export const {
  useGetContractorAlertRulesQuery,
  useLazyGetContractorAlertRulesQuery,
  useUpdateContractorAlertRuleMutation,
  useGetDeviceAlertRulesQuery,
  useLazyGetDeviceAlertRulesQuery,
  useUpdateDeviceAlertRuleMutation,
  useDeleteDeviceAlertRuleMutation,
} = alertRulesApi
